import * as THREE from "three";
import { ModelLocation as Location } from "./location";
import * as Extension from "./beacon-loader-extension";
import { Beacon } from "./beacon";
import { Category } from "./category";
import { DistanceModel, getNeededDistanceModel } from "./beacon-distance-model";

export function loadBeaconModel(parameters: {
    model: THREE.Object3D,
    beacon: Beacon,
    category: Category,
    initialLocation: Location,
    defaultImageURL: string,
    modelFailed: (error: string) => void,
    modelLoaded: (model: THREE.Object3D) => void
}) {
    const model = parameters.model;
    const beacon = parameters.beacon;
    const category = parameters.category;
    const defaultImageURL = parameters.defaultImageURL;
    const modelFailed = parameters.modelFailed;
    const modelLoaded = parameters.modelLoaded;

    const beaconImage = beacon.beacon_image == null ? category.beacon_image : beacon.beacon_image;
    // FUTURE: const showDistance = beacon.show_distance == null ? category.show_distance : beacon.show_distance
    const showDistance = category.show_distance;

    const hexColor = Number(`0x${category.color}`);

    // TODO: Setting precise to true will cause unwanted altitude results from the planet. This is producing a slightly different size every time.
    const modelBoundingBox = new THREE.Box3().setFromObject(model);

    model.userData.maxBoundingLength = Math.max(
        modelBoundingBox.max.x - modelBoundingBox.min.x,
        modelBoundingBox.max.y - modelBoundingBox.min.y,
        modelBoundingBox.max.z - modelBoundingBox.min.z
    );

    let distanceModel: DistanceModel | null = null;
    if (showDistance) {
        distanceModel = getNeededDistanceModel({
            cameraLocation: parameters.initialLocation,
            beaconLocation: { latitude: beacon.latitude, longitude: beacon.longitude },
            useYards: category.use_yards,
            userData: model.userData
        });
    }

    // const tessellateModifier = new TessellateModifier(8);
    var needsToLoadTexture = false;

    function findObjectsByName(name: string): THREE.Object3D[] {
        return Extension.findModelObjectsByName(model, name);
    }

    function findObjectsByPrefix(namePrefix: string): THREE.Object3D[] {
        return Extension.findModelObjectsByPrefix(model, namePrefix);
    }

    function findMeshesByName(name: string): THREE.Mesh[] {
        return Extension.findModelMeshesByName(model, name);
    }

    function findMeshesByPrefix(namePrefix: string): THREE.Mesh[] {
        return Extension.findModelMeshesByPrefix(model, namePrefix);
    }

    function meshHierarchy(name: string): THREE.Mesh[] {
        return Extension.modelMeshHierarchy(model, name);
    }

    function loadBeaconHead() {
        // Changes all textures in the beaconHeadBorder hierarchy
        meshHierarchy("beaconHeadBorder").forEach(mesh => {
            // TODO: From iOS, how to replicate this?
            //material.lightingModel = .physicallyBased

            mesh.material = new THREE.MeshPhysicalMaterial({
                color: hexColor,
                side: THREE.DoubleSide,
                roughness: 0.5
            });
        });

        // A version of the beacon head border to be smoothed by the GPU
        meshHierarchy("beaconHeadRoughBorder").forEach(mesh => {
            // TODO: From iOS, how to replicate this?
            //material.lightingModel = .physicallyBased
            //node.geometry?.subdivisionLevel = 1

            mesh.material = new THREE.MeshPhysicalMaterial({
                color: hexColor,
                side: THREE.DoubleSide,
                roughness: 0.5
            });

            if (mesh.geometry) {
                mesh.geometry = Extension.smoothGeometry(mesh.geometry);
            }
        });

        findMeshesByPrefix("beaconFaceBkg").forEach(mesh => {
            // 0xC0C0C0
            mesh.material = new THREE.MeshPhysicalMaterial({ color: 0xFFFFFF });
        });

        findMeshesByPrefix("beaconFaceImage").forEach(mesh => {
            if (needsToLoadTexture == false) {
                needsToLoadTexture = true;
            }

            function loadTexture(texture: THREE.Texture) {
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.y = -1;

                mesh.geometry = new THREE.CircleGeometry(
                    (mesh.geometry.boundingBox!.max.x - mesh.geometry.boundingBox!.min.x) / 2,
                    20
                );

                // TODO: From iOS, how to replicate this?
                // material.locksAmbientWithDiffuse = true

                mesh.material = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                    transparent: true,
                });
            }

            // TODO: Preload all images like iOS
            new THREE.TextureLoader().load(beaconImage, (texture) => {
                // Assumes 1 texture will be loaded on the model
                loadTexture(texture);

                modelLoaded(model);
            }, () => {
            }, () => {
                console.log(`Could not load beacon image: ${beaconImage}`);

                new THREE.TextureLoader().load(defaultImageURL, (texture) => {
                    loadTexture(texture);

                    modelLoaded(model);
                    console.log(`Loaded beacon deafult image due to error with: ${defaultImageURL}`);
                }, () => {
                }, () => {
                    console.log(`Could not load fallback beacon image: ${defaultImageURL}`);
                    modelFailed(`Could not load fallback beacon image: ${defaultImageURL}`);
                });
            });
        });

        findMeshesByPrefix("beaconBillboardBkg").forEach(mesh => {
            mesh.material = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
        });

        findMeshesByPrefix("beaconBillboardImage").forEach(mesh => {
            if (needsToLoadTexture == false) {
                needsToLoadTexture = true;
            }

            function loadTexture(texture: THREE.Texture) {
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.y = -1;

                // TODO: From iOS, how to replicate this?
                // material.locksAmbientWithDiffuse = true

                mesh.material = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                    transparent: true,
                });
            }

            // TODO: Preload all images like iOS
            new THREE.TextureLoader().load(beaconImage, (texture) => {
                // Assumes 1 texture will be loaded on the model
                loadTexture(texture);

                modelLoaded(model);
            }, () => {
            }, () => {
                console.log(`Could not load beacon image: ${beaconImage}`);

                new THREE.TextureLoader().load(defaultImageURL, (texture) => {
                    loadTexture(texture);

                    modelLoaded(model);
                    console.log(`Loaded beacon deafult image due to error with: ${defaultImageURL}`);
                }, () => {
                }, () => {
                    console.log(`Could not load fallback beacon image: ${defaultImageURL}`);
                    modelFailed(`Could not load fallback beacon image: ${defaultImageURL}`);
                });
            });
        });
    }

    function loadBeaconComplication() {
        findObjectsByName("complication").forEach(object => {
            object.removeFromParent();
        });
    }

    function loadBeaconPlacard() {
        // A planar placard border
        findMeshesByPrefix("placardBorder").forEach(mesh => {
            let material = new THREE.MeshPhysicalMaterial();
            material.color.setHex(hexColor);
            material.roughness = 0.5;

            // TODO: From iOS, how to replicate this?
            //material.lightingModel = .physicallyBased;

            mesh.material = material;
        });

        // A curved placard border
        findMeshesByPrefix("placardSurfaceBorder").forEach(mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({
                color: 0xFFFFFF,
                opacity: 0.75,
                side: THREE.DoubleSide,
                transparent: true
            });
        });

        findMeshesByPrefix("placardFaceBkg").forEach(mesh => {
            mesh.material = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
        });

        // Adds a 3D text node to a planar placard
        findMeshesByPrefix("placardText").forEach(mesh => {
            // Truncates text based on length
            let name = beacon.display_name;
            if (name.length > 20) {
                name = name.substring(0, 20) + "…";
            }

            Extension.addTextMesh({ mesh: mesh, name: name, fontSize: 6 });
        });

        findMeshesByPrefix("placardSurfaceTextA").forEach(mesh => {
            // Truncates text based on length
            let name = beacon.display_name;
            if (name.length > 24) {
                name = name.substring(0, 24) + "…";
            }

            Extension.addCurvedTextMesh({ mesh: mesh, name: name, fontSize: 6 });
        });

        findMeshesByPrefix("placardSurfaceTextB").forEach(mesh => {
            if (distanceModel) {
                Extension.addCurvedTextMesh({ mesh: mesh, name: `${distanceModel.distance} ${distanceModel.unitLong}`, fontSize: 5 });
            } else {
                // Truncates text based on length
                let name = beacon.display_name;
                if (name.length > 24) {
                    name = name.substring(0, 24) + "…";
                }

                Extension.addCurvedTextMesh({ mesh: mesh, name: name, fontSize: 6 });
            }
        });
    }

    function loadBeaconPointer() {
        if (showDistance) {
            findObjectsByName("pointerDefault").forEach(object => {
                object.removeFromParent();
            });

            findMeshesByName("pointerComplicationBorder").forEach(mesh => {
                // TODO: From iOS, how to replicate this?
                //material.lightingModel = .physicallyBased;

                mesh.material = new THREE.MeshPhysicalMaterial({
                    color: hexColor,
                    roughness: 0.5
                });
            });

            findMeshesByName("pointerComplicationBkg").forEach(mesh => {
                mesh.material = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
            });

            if (distanceModel) {
                const distanceNumberMeshes = findMeshesByName("pointerComplicationDistanceNumber");
                if (distanceNumberMeshes.length > 0) {
                    let distanceNumberMesh = distanceNumberMeshes[0];

                    Extension.addTextMesh({ mesh: distanceNumberMesh, name: `${distanceModel.distance}`, fontSize: 5 });

                    const distanceUnitMeshes = findMeshesByName("pointerComplicationDistanceUnit");

                    let distanceUnitMesh = distanceUnitMeshes.length > 0 ? distanceUnitMeshes[0] : null;
                    if (distanceUnitMesh) {
                        Extension.addTextMesh({ mesh: distanceUnitMesh, name: distanceModel.unitShort, fontSize: 4 });
                    }
                }
            }
        } else {
            findMeshesByName("pointerDefault").forEach(mesh => {
                // TODO: From iOS, how to replicate this?
                //material.lightingModel = .physicallyBased

                mesh.material = new THREE.MeshPhysicalMaterial({
                    color: hexColor,
                    roughness: 0.5
                });
            });

            findObjectsByPrefix("pointerComplication").forEach(object => {
                object.removeFromParent();
            });
        }
    }

    loadBeaconHead();
    loadBeaconPlacard();
    loadBeaconComplication();
    loadBeaconPointer();

    if (needsToLoadTexture == false) {
        // console.log(`TextureLoader had nothing to load: ${beaconName}`)

        modelLoaded(model);
    }
}

// Web AR Only
export function updateBeaconModelLocation(parameters: { model: THREE.Object3D, cameraLocation: Location, beaconLocation: Location, useYards: boolean }) {
    const model = parameters.model;
    const distanceModel = getNeededDistanceModel({
        cameraLocation: parameters.cameraLocation,
        beaconLocation: parameters.beaconLocation,
        useYards: parameters.useYards,
        userData: model.userData
    });

    if (distanceModel) {
        Extension.findModelMeshesByName(model, "pointerComplicationDistanceNumber").forEach(mesh => {
            mesh.children.forEach(child => { child.removeFromParent(); });

            Extension.addTextMesh({ mesh: mesh, name: `${distanceModel.distance}`, fontSize: 5 });

            Extension.findModelMeshesByName(model, "pointerComplicationDistanceUnit").forEach(mesh => {
                mesh.children.forEach(child => { child.removeFromParent(); });

                Extension.addTextMesh({ mesh: mesh, name: distanceModel.unitShort, fontSize: 4 });
            });
        });

        Extension.findModelMeshesByPrefix(model, "placardSurfaceTextB").forEach(mesh => {
            mesh.children.forEach(child => { child.removeFromParent(); });

            Extension.addCurvedTextMesh({ mesh: mesh, name: `${distanceModel.distance} ${distanceModel.unitLong}`, fontSize: 5 });
        });
    }
}

// Beacon Blender Only
export function updateBeaconModelName(parameters: { model: THREE.Object3D, beaconName: string, showDistance: boolean }) {
    const model = parameters.model;
    const beaconName = parameters.beaconName;
    const showDistance = parameters.showDistance;
    
    // Adds a 3D text node to a planar placard
    Extension.findModelMeshesByPrefix(model, "placardText").forEach(mesh => {
        mesh.children.forEach(child => { child.removeFromParent(); });

        // Truncates text based on length
        let name = beaconName;
        if (name.length > 20) {
            name = name.substring(0, 20) + "…";
        }

        Extension.addTextMesh({ mesh: mesh, name: name, fontSize: 6 });
    });

    Extension.findModelMeshesByPrefix(model, "placardSurfaceTextA").forEach(mesh => {
        mesh.children.forEach(child => { child.removeFromParent(); });

        // Truncates text based on length
        let name = beaconName;
        if (name.length > 24) {
            name = name.substring(0, 24) + "…";
        }

        Extension.addCurvedTextMesh({ mesh: mesh, name: name, fontSize: 6 });
    });

    Extension.findModelMeshesByPrefix(model, "placardSurfaceTextB").forEach(mesh => {
        if (showDistance == false) {
            mesh.children.forEach(child => { child.removeFromParent(); });

            // Truncates text based on length
            let name = beaconName;
            if (name.length > 24) {
                name = name.substring(0, 24) + "…";
            }

            Extension.addCurvedTextMesh({ mesh: mesh, name: name, fontSize: 6 });
        }
    });
}